import React from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import styles from './StatusLine.module.css';

/**
 * 글 속의 숫자(행 수)를 도드라지게 — 「몇 행이 반환·반영됐는지」 가 한눈에 보이게(사용자 요청 2026-10-26).
 * 문구는 번역 글 그대로 두고 숫자 조각만 감싼다(언어마다 어순이 달라 숫자 자리를 따로 정하지 않는다).
 */
export function emphasizeNumbers(text, cls) {
    const parts = String(text == null ? '' : text).split(/(\d[\d,]*)/);
    return parts.map((p, i) => (i % 2 === 1
        // eslint-disable-next-line react/no-array-index-key -- 글 조각의 순서가 곧 정체다
        ? <b key={i} className={cls}>{p}</b>
        : p));
}

const StatusLine = ({
    executionTime = null, // 이제 totalExecutionTime이 됨
    currentFetchTime = null, // 현재 fetch 시간 추가
    clientExecutionTime = null,
    message = null,
    rowCount = 0,
    isLoading = false,
    hasMoreData = false,
}) => {
    const { t } = useTranslation();
    let statusText = '---';
    let rowCountDisplay = t('common:sl.nRows', { n: Number(rowCount || 0).toLocaleString('en-US') });
    let loadingIndicator = isLoading ? t('common:sl.loadingMore') : '';
    let hasMoreIndicator = (executionTime !== null || message) && !isLoading && !hasMoreData && rowCount > 0 ? t('common:sl.allLoaded') : '';

    if (message) {
        statusText = message;
        // DML 메시지 경우, rowCount가 rowsAffected를 의미할 수 있으므로 `rowCount 행` 표시 유지 또는 수정
        // 여기서는 message가 있으면 statusText에 우선 할당하고, rowCount는 별도로 처리
    } else if (executionTime !== null) {
        statusText = t('common:sl.done');
    } 
    // 둘 다 없으면 statusText는 '---'

    // 메시지(DML)가 있거나, 실행시간(SELECT)이 있거나, 최초 로딩이 아닌 추가 로딩 중일 때만 rowCount 표시
    // 또는, rowCount가 0보다 클 때 항상 표시하도록 조정 가능
    const showRowCount = message || executionTime !== null || isLoading || rowCount > 0;

    // 시간 정보가 있는지 확인
    const hasTimeInfo = currentFetchTime !== null || executionTime !== null || clientExecutionTime !== null;

    return (
        <div className={styles.statusLineContainer}>
            <span>{emphasizeNumbers(statusText, styles.num)}</span>
            {showRowCount && (
                <span className={styles.rowCount}>
                    {emphasizeNumbers(rowCountDisplay, styles.num)}
                    {hasMoreIndicator && <span className={styles.hasMoreIndicator} style={{ marginLeft: '5px' }}>{hasMoreIndicator}</span>}
                    {hasTimeInfo && <span className={styles.separator}> | </span>}
                    {currentFetchTime !== null && (
                        <span className={styles.timeInfo}>{t('common:sl.currentFetch')} {Number(currentFetchTime).toFixed(1)}ms</span>
                    )}
                    {executionTime !== null && (
                        <span className={styles.timeInfo}>
                            {currentFetchTime !== null && <span className={styles.separator}> | </span>}
                            {t('common:sl.totalDbTime')} {Number(executionTime).toFixed(1)}ms
                        </span>
                    )}
                    {clientExecutionTime !== null && (
                        <span className={styles.timeInfo}>
                            {(currentFetchTime !== null || executionTime !== null) && <span className={styles.separator}> | </span>}
                            {t('common:sl.clientTime')} {Number(clientExecutionTime * 1000).toFixed(1)}ms
                        </span>
                    )}
                </span>
            )}
            {loadingIndicator && <span className={styles.loadingIndicator}>{loadingIndicator}</span>}
        </div>
    );
};

StatusLine.propTypes = {
    executionTime: PropTypes.number, // totalExecutionTime
    currentFetchTime: PropTypes.number, // 현재 fetch 시간 추가
    clientExecutionTime: PropTypes.number,
    message: PropTypes.string,
    rowCount: PropTypes.number,
    isLoading: PropTypes.bool,    // isLoading prop 타입 추가
    hasMoreData: PropTypes.bool, // hasMoreData prop 타입 추가
};

export default StatusLine; 